import { useEffect, useState, useRef } from "react";

/**
 * Reveals characters one-at-a-time. Respects prefers-reduced-motion
 * (jumps straight to the final string on mobile / reduced-motion).
 *
 * @param {string|Array<{text:string,cls?:string,pause?:number}>} script
 * @param {object} opts
 *   - speed: ms per character (default 22)
 *   - startDelay: ms before typing starts (default 0)
 *   - loop: boolean (default false)
 *   - active: only type when true (default true)
 *   - reducedMotion: force instant reveal (default auto-detect)
 */
export function useTypewriter(script, opts = {}) {
    const {
        speed = 22,
        startDelay = 0,
        loop = false,
        active = true,
        reducedMotion = null,
    } = opts;

    const parts = Array.isArray(script) ? script : [{ text: String(script) }];
    const full = parts.map((p) => p.text).join("");

    const prefersReduced = (() => {
        if (typeof reducedMotion === "boolean") return reducedMotion;
        if (typeof window === "undefined") return false;
        return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    })();

    const [out, setOut] = useState(prefersReduced || !active ? full : "");
    const [done, setDone] = useState(prefersReduced || !active);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!active) return;
        if (prefersReduced) {
            setOut(full);
            setDone(true);
            return;
        }
        setOut("");
        setDone(false);

        let i = 0;
        const start = setTimeout(function tick() {
            if (i >= full.length) {
                setDone(true);
                if (loop) {
                    timerRef.current = setTimeout(() => {
                        i = 0;
                        setOut("");
                        setDone(false);
                        timerRef.current = setTimeout(tick, speed);
                    }, 3000);
                }
                return;
            }
            i += 1;
            setOut(full.slice(0, i));
            timerRef.current = setTimeout(tick, speed);
        }, startDelay);

        timerRef.current = start;
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [full, speed, startDelay, loop, active, prefersReduced]);

    return { text: out, done };
}
